import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  Download, 
  Calendar, 
  Filter, 
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Users,
  FileText,
  Home,
  CreditCard,
  Vote,
  Building,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react';
import { useUser } from '@/store/hooks';
import reportService from '@/services/reportService';
import { downloadComplaintReportExcel, downloadMemberReportExcel } from '@/services/exportService';
import { 
  DashboardReport, 
  ReportFilters, 
  ComplaintReport,
  UserReport,
  DepartmentReport,
  EventReport,
  BusinessReport,
  ChargeReport,
  ElectionReport
} from '@/types';

const ReportsDashboard: React.FC = () => {
  const user = useUser();
  const [dashboardData, setDashboardData] = useState<DashboardReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ReportFilters>({
    date_from: reportService.getDateRange('month').from,
    date_to: reportService.getDateRange('month').to
  });
  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, [filters]);

  const fetchDashboardData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await reportService.getDashboardReport(filters);
      setDashboardData(data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePeriodChange = (period: 'week' | 'month' | 'quarter' | 'year') => {
    setSelectedPeriod(period);
    const dateRange = reportService.getDateRange(period);
    setFilters(prev => ({
      ...prev,
      date_from: dateRange.from,
      date_to: dateRange.to
    }));
  };

  const handleExport = async (type: string, format: 'json' | 'csv' | 'xlsx') => {
    setIsExporting(true);
    try {
      if (format === 'xlsx' && type === 'complaints') {
        await downloadComplaintReportExcel(filters as Record<string, string | undefined>);
      } else if (format === 'xlsx' && type === 'users') {
        await downloadMemberReportExcel(filters as Record<string, string | undefined>);
      } else {
        await reportService.downloadReport(type, format, filters);
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      setError('Failed to export report');
    } finally {
      setIsExporting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'assigned': return <Users className="h-4 w-4 text-blue-500" />;
      case 'in_progress': return <RefreshCw className="h-4 w-4 text-purple-500" />;
      case 'on_hold': return <AlertCircle className="h-4 w-4 text-orange-500" />;
      case 'resolved': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'closed': return <XCircle className="h-4 w-4 text-gray-500" />;
      default: return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'low': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'urgent': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Reports</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <BarChart3 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Data Available</h2>
          <p className="text-gray-600">No report data found for the selected period.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Reports Dashboard</h1>
              <p className="mt-2 text-gray-600">
                Comprehensive analytics and insights for NFCRWA management
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={fetchDashboardData}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </button>
              <div className="relative">
                <select
                  value={selectedPeriod}
                  onChange={(e) => handlePeriodChange(e.target.value as any)}
                  className="appearance-none bg-white border border-gray-300 rounded-md px-4 py-2 pr-8 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="week">Last Week</option>
                  <option value="month">Last Month</option>
                  <option value="quarter">Last Quarter</option>
                  <option value="year">Last Year</option>
                </select>
                <Calendar className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Complaints */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FileText className="h-6 w-6 text-blue-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Complaints</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {dashboardData.complaints.total_complaints}
                    </dd>
                  </dl>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-green-600">
                    {dashboardData.complaints.resolved_complaints} resolved
                  </span>
                  <span className="text-yellow-600">
                    {dashboardData.complaints.pending_complaints} pending
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Users */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-6 w-6 text-green-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {dashboardData.users.total_users}
                    </dd>
                  </dl>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-green-600">
                    {dashboardData.users.active_users} active
                  </span>
                  <span className="text-blue-600">
                    {dashboardData.users.new_users_this_month} new this month
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Departments */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Building className="h-6 w-6 text-purple-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Departments</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {dashboardData.departments.total_departments}
                    </dd>
                  </dl>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-green-600">
                    {dashboardData.departments.active_departments} active
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Events */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Calendar className="h-6 w-6 text-orange-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Events</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {dashboardData.events.total_events}
                    </dd>
                  </dl>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-blue-600">
                    {dashboardData.events.upcoming_events} upcoming
                  </span>
                  <span className="text-gray-600">
                    {dashboardData.events.past_events} past
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Reports */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Complaints by Status */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Complaints by Status</h3>
              <button
                onClick={() => handleExport('complaints', 'csv')}
                disabled={isExporting}
                className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
              >
                <Download className="h-4 w-4 inline mr-1" />
                Export
              </button>
            </div>
            <div className="space-y-3">
              {Object.entries(dashboardData.complaints.complaints_by_status).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center">
                    {getStatusIcon(status)}
                    <span className="ml-2 text-sm font-medium text-gray-900 capitalize">
                      {status.replace('_', ' ')}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Complaints by Priority */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Complaints by Priority</h3>
              <button
                onClick={() => handleExport('priority-wise', 'csv')}
                disabled={isExporting}
                className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
              >
                <Download className="h-4 w-4 inline mr-1" />
                Export
              </button>
            </div>
            <div className="space-y-3">
              {Object.entries(dashboardData.complaints.complaints_by_priority).map(([priority, count]) => (
                <div key={priority} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(priority)}`}>
                      {priority}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Department Performance */}
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Department Performance</h3>
            <button
              onClick={() => handleExport('departments', 'csv')}
              disabled={isExporting}
              className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
            >
              <Download className="h-4 w-4 inline mr-1" />
              Export
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Complaints
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performance
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dashboardData.complaints.complaints_by_department.map((dept, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {dept.department_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {dept.count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ 
                              width: `${(dept.count / Math.max(...dashboardData.complaints.complaints_by_department.map(d => d.count))) * 100}%` 
                            }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-500">
                          {Math.round((dept.count / Math.max(...dashboardData.complaints.complaints_by_department.map(d => d.count))) * 100)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Monthly Trends */}
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Monthly Trends</h3>
            <button
              onClick={() => handleExport('time-range', 'csv')}
              disabled={isExporting}
              className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
            >
              <Download className="h-4 w-4 inline mr-1" />
              Export
            </button>
          </div>
          <div className="space-y-4">
            {dashboardData.complaints.complaints_by_month.map((month, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">{month.month}</span>
                <div className="flex items-center">
                  <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ 
                        width: `${(month.count / Math.max(...dashboardData.complaints.complaints_by_month.map(m => m.count))) * 100}%` 
                      }}
                    ></div>
                  </div>
                  <span className="text-sm text-gray-900 w-12 text-right">{month.count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Export Options */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Export Reports</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { type: 'complaints', label: 'Complaints Report', icon: FileText },
              { type: 'users', label: 'Users Report', icon: Users },
              { type: 'departments', label: 'Departments Report', icon: Building },
              { type: 'events', label: 'Events Report', icon: Calendar },
              { type: 'businesses', label: 'Business Report', icon: Home },
              { type: 'charges', label: 'Charges Report', icon: CreditCard },
              { type: 'elections', label: 'Elections Report', icon: Vote },
              { type: 'block-wise', label: 'Block-wise Report', icon: BarChart3 },
              { type: 'category-wise', label: 'Category-wise Report', icon: BarChart3 }
            ].map(({ type, label, icon: Icon }) => (
              <div key={type} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Icon className="h-5 w-5 text-gray-400 mr-2" />
                  <h4 className="text-sm font-medium text-gray-900">{label}</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(type === 'complaints' || type === 'users') && (
                    <button
                      onClick={() => handleExport(type, 'xlsx')}
                      disabled={isExporting}
                      className="flex-1 min-w-[4rem] text-xs px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      Excel
                    </button>
                  )}
                  <button
                    onClick={() => handleExport(type, 'csv')}
                    disabled={isExporting}
                    className="flex-1 min-w-[4rem] text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    CSV
                  </button>
                  <button
                    onClick={() => handleExport(type, 'json')}
                    disabled={isExporting}
                    className="flex-1 min-w-[4rem] text-xs px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50"
                  >
                    JSON
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsDashboard;
